import { html, nothing } from 'lit';
import LightElement from './light-element.js';
import { customElement, property, state } from 'lit/decorators.js';

export type ToastType = 'success' | 'error' | 'info' | 'warning';
export type ToastPosition = 'offsetbottom' | 'bottom';

@customElement('toast-notification')
export class ToastNotification extends LightElement {

	@property({ type: String }) headline = '';

	@property({ type: String }) message = '';

	@property({ type: String }) type: ToastType = 'info';

	@property({ type: String }) position: ToastPosition = 'offsetbottom';

	@property({ type: String }) href = '';

	@state() private open = false;

	@state() private timer: number | null = null;

	private remainingDuration = 0;

	private timerStart: number | null = null;

	show(headline: string, message = '', type: ToastType = 'info', duration = 3500, position: ToastPosition = 'offsetbottom', href = '') {
		this.headline = headline;
		this.message = message;
		this.type = type;
		this.open = true;
		this.position = position;
		this.href = href;
		this.remainingDuration = duration;
		if (this.timer) {
			clearTimeout(this.timer);
		}
		this.timerStart = Date.now();
		this.timer = window.setTimeout(() => this.close(), duration);
	}

	close() {
		this.open = false;
		if (this.timer) {
			clearTimeout(this.timer);
		}
		this.timer = null;
		this.timerStart = null;
	}

	private pauseTimer() {
		if (!this.timer || this.timerStart === null) return;
		clearTimeout(this.timer);
		this.timer = null;
		this.remainingDuration = Math.max(0, this.remainingDuration - (Date.now() - this.timerStart));
		this.timerStart = null;
	}

	private resumeTimer() {
		if (this.timer || !this.open) return;
		this.timerStart = Date.now();
		this.timer = window.setTimeout(() => this.close(), this.remainingDuration);
	}

	render() {
		const isClickable = !!this.href;
		const classes = `c-toast ${this.type} ${this.open ? 'open' : ''} c-toast--${this.position}${isClickable ? ' c-toast--clickable' : ''}`;
		return html`
			<div class=${classes}
				role="alert"
				data-test="toast-notification"
				@mouseenter=${this.pauseTimer}
				@mouseleave=${this.resumeTimer}
				@focusin=${this.pauseTimer}
				@focusout=${this.resumeTimer}
			>
				<button class="c-toast__close" @click=${() => this.close()} aria-label="Close">&times;</button>
				${isClickable ? html`
					<a class="c-toast__link" href=${this.href}>
						<span class="c-toast__headline">${this.headline}</span>
						${this.message ? html`<span class="c-toast__message">${this.message}</span>` : nothing}
					</a>
				` : html`
					<span class="c-toast__headline">${this.headline}</span>
					${this.message ? html`<span class="c-toast__message">${this.message}</span>` : nothing}
				`}
			</div>
		`;
	}
}

export default ToastNotification;
